const DAY_MS = 86_400_000;

const dateFmt = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  timeZone: 'UTC',
});
const dayFmt = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  timeZone: 'UTC',
});
const monthFmt = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  year: 'numeric',
  timeZone: 'UTC',
});
const timeFmt = new Intl.DateTimeFormat('en-GB', {
  hour: '2-digit',
  minute: '2-digit',
  timeZone: 'UTC',
});

function parse(iso: string | undefined): Date | undefined {
  if (!iso) return undefined;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? undefined : d;
}

/** "Mar 31, 2027" in UTC, the zone every API time is in; "–" when absent or invalid. */
export function formatDate(iso: string | undefined): string {
  const d = parse(iso);
  return d ? dateFmt.format(d) : '–';
}

/** "Oct 14" in UTC, for a date in a short label; "–" when absent or invalid. */
export function formatDay(iso: string | undefined): string {
  const d = parse(iso);
  return d ? dayFmt.format(d) : '–';
}

/** "Apr 2024" in UTC; "–" when absent or invalid. */
export function formatMonth(iso: string | undefined): string {
  const d = parse(iso);
  return d ? monthFmt.format(d) : '–';
}

/** "Oct 9, 02:14" in UTC; "–" when absent or invalid. */
export function formatDateTime(iso: string | undefined): string {
  const d = parse(iso);
  return d ? `${dayFmt.format(d)}, ${timeFmt.format(d)}` : '–';
}

/** "40 s ago", "18 min ago", "3 h ago" (under 48 h), then "5 d ago"; "never" when absent. */
export function formatAgo(iso: string | undefined, now: Date = new Date()): string {
  if (!iso) return 'never';
  const d = parse(iso);
  if (!d) return '–';
  const s = Math.max(0, Math.round((now.getTime() - d.getTime()) / 1000));
  if (s < 60) return `${s} s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 48) return `${h} h ago`;
  return `${Math.floor(h / 24)} d ago`;
}

/** Whole days from now until iso, rounded up; negative once it has passed. */
export function daysUntil(iso: string, now: Date = new Date()): number {
  return Math.ceil((new Date(iso).getTime() - now.getTime()) / DAY_MS);
}

/** Up to two initials from a name: "Riverside County DOT" -> "RC". */
export function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]!.toUpperCase())
    .join('');
}

/** "99.97%" with two decimals; "–" when absent (no data). */
export function formatPercent(v: number | undefined): string {
  return v === undefined ? '–' : `${v.toFixed(2)}%`;
}

/** Binary units: "22.4 GiB", "310 MiB". */
export function formatBytes(n: number): string {
  const units = ['B', 'KiB', 'MiB', 'GiB', 'TiB'];
  let v = n;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${i === 0 || v >= 100 ? Math.round(v) : v.toFixed(1)} ${units[i]}`;
}

/** "140 m" below one core, "1.25 cores" from one up. */
export function formatCpuMilli(m: number): string {
  if (m < 1000) return `${Math.round(m)} m`;
  const cores = m / 1000;
  return `${Number(cores.toFixed(2))} core${cores === 1 ? '' : 's'}`;
}

/** A length of time from seconds: "45 s", "12 min", "5 h 2 min", "3 d 4 h". */
export function formatDuration(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  if (s < 60) return `${s} s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  if (h < 24) return m % 60 ? `${h} h ${m % 60} min` : `${h} h`;
  const d = Math.floor(h / 24);
  return h % 24 ? `${d} d ${h % 24} h` : `${d} d`;
}

/** Length in code points, as the server counts text limits (Go runes); an emoji is one. */
export function charCount(s: string): number {
  return [...s].length;
}
